import React, { useCallback, useEffect, useState } from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';
import { ToastOffset } from '@fluentui/react-toast';
import { CopilotRecommendationBanner, ICopilotRecommendationBannerProps } from './CopilotRecommendationBanner';
import { CopilotThemesRecommendationDialog, ICopilotThemesRecommendationDialogProps } from '../CopilotThemesRecommendationDialog';
import { useBannerService } from './banner-service';

const DEFAULT_TOAST_OFFSET: ToastOffset = { horizontal: 260, vertical: 120 };

type PickedBannerProps = Pick<ICopilotRecommendationBannerProps, 'className' | 'narrow' | 'toastOffset' | 'onFetch' | 'onFetchFailed' | 'onFetchSucceeded' | 'onReset' | 'customStrings'>;

export interface ICopilotRecommendationBannerWithDialogProps extends PickedBannerProps, Pick<ICopilotThemesRecommendationDialogProps, 'onApply'> {
  applyRecommendationsResult?: 'ApplySucceeded' | 'ApplyFailed';
  eventLog?: {
    logBannerRefresh?: () => void;
    logBannerRefreshCompleted?: ICopilotRecommendationBannerProps['onBannerRefreshCompleted'];
    logFetch?: ICopilotThemesRecommendationDialogProps['onFetch'];
    logFetchSucceeded?: ICopilotThemesRecommendationDialogProps['onFetchSucceeded'];
  };
  onDialogFetchAssetsSucceeded: ICopilotThemesRecommendationDialogProps['onFetchAssetsSucceeded'];
  isNewLogo?: boolean;
}

const useCopilotRecommendationBannerWithDialogStyles = makeStyles({
  banner: {
    marginBottom: tokens.spacingVerticalS,
  },
});

/**
 * CopilotRecommendationBannerWithDialog is an opinionated version of the existing Banner and Dialog components.
 * It is configured with business logic that is common across the different campaign types.
 */
export const CopilotRecommendationBannerWithDialog: React.FC<ICopilotRecommendationBannerWithDialogProps> = React.memo((props) => {
  const classes = useCopilotRecommendationBannerWithDialogStyles();
  const { send, state } = useBannerService();
  const {
    applyRecommendationsResult,
    className = classes.banner,
    eventLog,

    onFetch,
    onFetchFailed,
    onFetchSucceeded,
    onReset,

    onApply,
    onDialogFetchAssetsSucceeded,

    customStrings,
    toastOffset = DEFAULT_TOAST_OFFSET,
    narrow,
    isNewLogo,
  } = props;

  const {
    logBannerRefresh,
    logBannerRefreshCompleted,
    logFetch,
    logFetchSucceeded,
  } = eventLog || {};

  const [showCopilotDialog, setShowCopilotDialog] = useState(false);

  const handleRefresh = useCallback(() => {
    logBannerRefresh?.();
    setShowCopilotDialog(true);
  }, [logBannerRefresh, setShowCopilotDialog]);

  const handleCopilotDialogClose = useCallback(() => {
    setShowCopilotDialog(false);
  }, [setShowCopilotDialog]);

  const handleApply = useCallback<ICopilotThemesRecommendationDialogProps['onApply']>((selectedThemes, selectedThemesAndAssets) => {
    // put banner in applying progress state
    send('Apply');

    return Promise.resolve(onApply(selectedThemes, selectedThemesAndAssets)).then(() => {
      // close the dialog after applying
      handleCopilotDialogClose();
    });
  }, [onApply, handleCopilotDialogClose]);

  useEffect(() => {
    if (state.value === 'Applying' && applyRecommendationsResult) {
      // if applying and a result has been returned, put banner in applying success/fail state
      send(applyRecommendationsResult);
    }
  }, [applyRecommendationsResult, state.value]);

  return (
    <>
      <CopilotRecommendationBanner
        className={className}
        isToastEnabled
        onBannerRefreshCompleted={logBannerRefreshCompleted}
        onFetch={onFetch}
        onFetchFailed={onFetchFailed}
        onFetchSucceeded={onFetchSucceeded}
        onRefresh={handleRefresh}
        onReset={onReset}
        shouldFetchRecommendations
        toastOffset={toastOffset}
        narrow={narrow}
        customStrings={customStrings}
      />
      {showCopilotDialog && (
        <CopilotThemesRecommendationDialog
          onApply={handleApply}
          onClosed={handleCopilotDialogClose}
          onFetch={logFetch}
          onFetchAssetsSucceeded={onDialogFetchAssetsSucceeded}
          onFetchSucceeded={logFetchSucceeded}
          open
          isNewLogo={isNewLogo}
        />
      )}
    </>
  );
});
